import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useAuth } from '../hooks/useAuth';
import { Loader2, Mail, Lock, Eye, EyeOff, AlertCircle, ShieldAlert } from 'lucide-react';
import { Button, Input, Label, toast } from '@repo/ui';
import { useTranslation } from 'react-i18next';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'validation.required' })
    .pipe(
      z
        .string()
        .regex(
          /^[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@[a-zA-Z0-9][a-zA-Z0-9-]*(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
          'validation.invalidEmail'
        )
    ),
  password: z.string().min(1, { message: 'validation.required' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const DEFAULT_LOCK_SECONDS = 120;

interface LoginFormProps {
  onSuccess?: () => void;
  onForgotPassword?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onForgotPassword }) => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [lockRemainingSeconds, setLockRemainingSeconds] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: async (values) => {
      const result = await loginSchema.safeParseAsync(values);

      if (result.success) {
        return { values: result.data, errors: {} };
      }

      const errors = result.error.issues.reduce((acc: any, issue) => {
        const path = issue.path[0];
        if (path) {
          acc[path] = { type: issue.code, message: issue.message };
        }
        return acc;
      }, {});

      return { values: {}, errors };
    },
    defaultValues: { email: '', password: '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isLocked || lockRemainingSeconds <= 0) return;

    const timer = window.setInterval(() => {
      setLockRemainingSeconds((seconds) => {
        if (seconds <= 1) {
          setIsLocked(false);
          setRemainingAttempts(null);
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isLocked, lockRemainingSeconds]);

  const lockForm = (seconds = DEFAULT_LOCK_SECONDS) => {
    setIsLocked(true);
    setRemainingAttempts(0);
    setLockRemainingSeconds(Math.max(1, seconds));
  };

  const formatLockTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes <= 0) return `${remainingSeconds}s`;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const onSubmit = async (data: LoginFormValues) => {
    if (isLocked) return; // hard-block while account is locked

    try {
      setRemainingAttempts(null);
      await login(data);
      toast.success(t('auth.loginSuccess'));
      onSuccess?.();
    } catch (err: any) {
      const status = err.response?.status;
      const responseData = err.response?.data;

      // 429 — throttler
      if (status === 429) {
        toast.error(t('auth.tooManyRequests'));
        return;
      }

      // 403 — account locked (ForbiddenException from backend)
      if (status === 403) {
        const message = responseData?.message ?? responseData;
        const retryAfterSeconds = responseData?.retryAfterSeconds;

        if (typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0) {
          lockForm(retryAfterSeconds);
        } else {
          setIsLocked(false);
          setLockRemainingSeconds(0);
          setRemainingAttempts(null);
        }

        toast.error(Array.isArray(message) ? message[0] : message || t('auth.invalidCredentials'));
        return;
      }

      // 401 — invalid credentials, may include remainingAttempts
      const message = responseData?.message ?? responseData;
      const remaining = responseData?.remainingAttempts;

      if (typeof remaining === 'number') {
        setRemainingAttempts(remaining);

        // Account just got locked on this very attempt
        if (remaining === 0) {
          lockForm(responseData?.retryAfterSeconds);
          toast.error(t('auth.accountLocked', 'تم قفل الحساب مؤقتاً. حاول مرة أخرى بعد دقيقتين.'));
          return;
        }
      }

      toast.error(Array.isArray(message) ? message[0] : message || t('auth.invalidCredentials'));
    }
  };

  const onError = (errors: any) => {
    console.debug('Validation failed', errors);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(onSubmit, onError)(e);
  };

  return (
    <div className="w-full">
      <form onSubmit={handleFormSubmit} className="space-y-6">
        <div className="space-y-4">
          <Label htmlFor="email" className="mb-1.5 block">
            {t('auth.email')}
          </Label>
          <Input
            {...register('email')}
            id="email"
            type="email"
            placeholder={t('auth.emailPlaceholder')}
            icon={<Mail className="h-4 w-4" />}
            error={!!errors.email}
            className="h-11"
          />
          {errors.email && (
            <div className="flex items-center gap-2 text-red-500 mt-1.5 pl-1 fade-in slide-in-from-top-1 duration-200">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm font-medium">{t(errors.email.message as string)}</p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <Label htmlFor="password" className="mb-1.5 block">
            {t('auth.password')}
          </Label>
          <Input
            {...register('password')}
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder={t('auth.passwordPlaceholder')}
            icon={<Lock className="h-4 w-4" />}
            rightElement={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
            }
            error={!!errors.password}
            className="h-11"
          />
          {errors.password && (
            <div className="flex items-center gap-2 text-red-500 mt-1.5 pl-1 fade-in slide-in-from-top-1 duration-200">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm font-medium">
                {t(errors.password.message as string, { count: 6 })}
              </p>
            </div>
          )}
        </div>

        {/* Remaining attempts warning */}
        {remainingAttempts !== null && remainingAttempts > 0 && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3 animate-in fade-in slide-in-from-top-1 duration-200">
            <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">
              {t('auth.attemptsRemaining', { count: remainingAttempts })}
            </p>
          </div>
        )}

        {/* Account locked banner */}
        {isLocked && (
          <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800 px-4 py-3 animate-in fade-in slide-in-from-top-1 duration-200">
            <ShieldAlert className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300 font-medium">
              {t('auth.accountLocked')}{' '}
              {lockRemainingSeconds > 0 && `(${formatLockTime(lockRemainingSeconds)})`}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between">
          {onForgotPassword && (
            <Button
              variant="link"
              className="px-0 cursor-pointer font-semibold h-auto"
              onClick={onForgotPassword}
              type="button"
            >
              {t('auth.forgotPassword')}
            </Button>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting || isLocked}
          className="w-full p bg-[oklch(0.34_0.17_262.91)] hover:bg-[oklch(0.34_0.17_262.91)]/90 text-white shadow-lg shadow-[oklch(0.34_0.17_262.91)]/20 -mt-3 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          size="sm"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mx-2" />
              {t('auth.signingIn')}
            </>
          ) : (
            t('auth.login')
          )}
        </Button>
      </form>
    </div>
  );
};
